import React, { useState } from 'react';
import { Artist } from './types';

interface MissingPhotosAlertProps {
  artists: Artist[];
  onDismiss: (permanent?: boolean) => void;
  onViewArtists?: () => void;
}

const MissingPhotosAlert: React.FC<MissingPhotosAlertProps> = ({ 
  artists, 
  onDismiss, 
  onViewArtists 
}) => {
  const [isDismissing, setIsDismissing] = useState(false);

  const handleDismissPermanently = async () => {
    setIsDismissing(true);
    try {
      // Set localStorage
      localStorage.setItem('missingPhotosAlertDismissed', 'true');
      onDismiss(true);
    } catch (error) {
      console.error('Error dismissing alert:', error);
    } finally {
      setIsDismissing(false);
    }
  };

  const handleRemindLater = () => {
    onDismiss(false);
  };

  const handleViewArtists = () => {
    if (onViewArtists) {
      onViewArtists();
    }
    onDismiss(false);
  };

  return (
    <div className="fixed inset-0 z-50 w-full h-full overflow-y-auto bg-gray-600 bg-opacity-50">
      <div className="relative w-full max-w-md p-6 mx-auto bg-white border rounded-lg shadow-xl top-20">
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center">
            <div className="flex items-center justify-center flex-shrink-0 w-12 h-12 rounded-full bg-amber-100">
              <svg
                className="w-6 h-6 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>
          <button
            onClick={handleRemindLater}
            className="text-gray-400 transition-colors hover:text-gray-600"
            disabled={isDismissing}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="mt-3">
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            Artists Missing Photos
          </h3>
          <p className="mb-4 text-sm text-gray-600">
            {artists.length} {artists.length === 1 ? 'artist' : 'artists'} in your roster {artists.length === 1 ? 'is' : 'are'} missing profile photos.
          </p>
          
          {/* Artists List */}
          <div className="p-3 mb-4 rounded-lg bg-gray-50">
            <div className="overflow-y-auto max-h-32">
              <ul className="space-y-1">
                {artists.slice(0, 5).map((artist) => (
                  <li key={artist.id} className="flex items-center text-sm text-gray-700">
                    <span className="w-2 h-2 mr-2 bg-gray-400 rounded-full"></span>
                    {artist.artistName}
                  </li>
                ))}
                {artists.length > 5 && (
                  <li className="text-sm italic text-gray-500">
                    ...and {artists.length - 5} more
                  </li>
                )}
              </ul>
            </div>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex flex-col space-y-2 sm:flex-row sm:space-y-0 sm:space-x-3">
          <button
            onClick={handleViewArtists}
            className="flex-1 px-4 py-2 text-sm font-medium text-white transition-colors bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            disabled={isDismissing}
          >
            View Artists
          </button>
          <button
            onClick={handleRemindLater}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 transition-colors bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            disabled={isDismissing}
          >
            Remind Later
          </button>
          <button
            onClick={handleDismissPermanently}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-600 transition-colors bg-gray-100 border border-gray-300 rounded-md shadow-sm hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50"
            disabled={isDismissing}
          >
            {isDismissing ? 'Dismissing...' : 'Dismiss Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MissingPhotosAlert;